import { Box, Button, Typography } from '@mui/material'
import { RecoverUsernameForm, RecoveryForm, SignInForm, SignUpForm } from '@zippie/did-react-components'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'

import { ReactComponent as AirImpactLogo } from '../assets/img/logo/airimpact-logo-colorful-with-text.svg'
import { useZippieIdContext } from '../hooks/useZippieId'
import { ReactComponent as WelcomeImg } from '../icons/welcome-img.svg'

enum viewNames {
  signIn = 'signIn',
  signUp = 'signUp',
  forgotUsername = 'forgotUsername',
  forgotPassword = 'forgotPassword',
}
// eslint-disable-next-line import/no-anonymous-default-export
export default function () {
  const [viewName, setViewName] = useState<viewNames>()
  const [isTransferLink, setIsTransferLink] = useState(false)

  const { onUserSignIn, onUserSignUp } = useZippieIdContext()
  const { inviteCode, workspaceId } = useParams<{
    inviteCode: string
    projectId: string
    workspaceId: string
    communityId: string
  }>()
  const navigate = useNavigate()
  const location = useLocation()

  const queryParams = new URLSearchParams(location.search)
  const transferId = queryParams.get('transferId')
  const code = queryParams.get('code')

  const handleDialogOpenClick = (viewName: viewNames) => {
    setViewName(viewName)
  }

  const onForgotUsernameClick = () => {
    setViewName(viewNames.forgotUsername)
  }

  const onForgotPasswordClick = () => {
    setViewName(viewNames.forgotPassword)
  }

  const onUserLoginSuccess = () => {
    if (isTransferLink) {
      navigate(`/transfer_ownership/${workspaceId}?transferId=${transferId}&code=${code}`)
    } else {
      onUserSignIn()
    }
  }

  const onUserSignUpSuccess = () => {
    if (isTransferLink) {
      navigate(`/transfer_ownership/${workspaceId}?transferId=${transferId}&code=${code}`)
    } else {
      onUserSignUp()
    }
  }

  const renderComponent = () => {
    switch (viewName) {
      case viewNames.signUp: {
        return (
          <SignUpForm
            onSignInButtonClick={() => setViewName(viewNames.signIn)}
            onSignUpComplete={onUserSignUpSuccess}
            onForgotPasswordClick={onForgotPasswordClick}
            termsLink="https://airimpact.co/platform-privacy-policy"
          />
        )
      }

      case viewNames.signIn:
        return (
          <SignInForm
            onSignInComplete={onUserLoginSuccess}
            onForgotUsernameClick={onForgotUsernameClick}
            onForgotPasswordClick={onForgotPasswordClick}
            onSignUpClick={() => setViewName(viewNames.signUp)}
            loadingType="circular"
          />
        )

      case viewNames.forgotUsername:
        return (
          <RecoverUsernameForm
            loadingType="circular"
            onBackButtonClick={() => setViewName(viewNames.signIn)}
            onRecoveryComplete={() => setViewName(viewNames.signIn)}
          />
        )

      case viewNames.forgotPassword:
        return (
          <RecoveryForm
            loadingType="circular"
            onBackButtonClick={() => setViewName(viewNames.signIn)}
            onRecoveryComplete={() => setViewName(viewNames.signIn)}
          />
        )

      default:
        return <></>
    }
  }

  useEffect(() => {
    if (workspaceId && transferId && code) {
      setIsTransferLink(true)
    }

    if (inviteCode) {
      handleDialogOpenClick(viewNames.signUp)
    } else {
      handleDialogOpenClick(viewNames.signIn)
    }
  }, [inviteCode, workspaceId, code])

  return (
    <Box display="flex" minHeight="100vh" boxSizing="border-box">
      <Box
        display="flex"
        height="100vh"
        p="53px 73px"
        flexDirection="column"
        boxSizing="border-box"
        style={{
          background: `linear-gradient(0deg, #F5F8F6, #F5F8F6), linear-gradient(0deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3))`,
        }}
        mr={3}
      >
        <AirImpactLogo />

        <Box display="flex" flexDirection="column" mt="auto" mb="auto" maxWidth="620px" width="100%">
          <svg width="26" height="33" viewBox="0 0 26 33" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M23.4754 0.377576C23.7505 0.181807 24.0672 -0.0785937 24.4161 0.0225694C24.6625 0.395374 24.7077 0.872152 24.7988 1.30865C25.1381 3.50989 25.2214 5.74672 25.2353 7.97418C25.231 10.3047 25.1138 12.6408 24.7632 14.9423C24.5315 16.4007 24.2191 17.8582 23.6576 19.2145C23.3096 20.0679 22.7985 20.8266 22.2839 21.5703C20.5673 24.0291 18.219 26.0215 15.4924 26.99C12.4759 28.0991 9.19121 27.9679 6.11221 27.2448C5.3416 28.5965 4.6109 29.9753 3.9479 31.3925C3.74743 31.8103 3.61726 32.3039 3.2467 32.5915C2.66527 33.0551 1.86862 33.0907 1.19693 32.865C0.719638 32.6992 0.244078 32.3648 0.0548951 31.844C-0.0882937 31.2576 0.0757231 30.6488 0.237136 30.0868C0.722242 28.5562 1.53278 27.1755 2.40319 25.8697C3.22067 24.3973 4.28634 23.1056 5.42057 21.9103C7.88341 19.3438 10.775 17.2943 13.8253 15.6354C16.4097 14.2088 18.6703 12.0816 20.2671 9.45978C21.0464 8.19243 21.6929 6.82017 22.1112 5.36361C20.4033 8.33106 18.0915 10.9014 15.3761 12.8038C13.9798 13.7995 12.4099 14.4505 10.9529 15.3282C7.9511 17.0929 5.1394 19.2913 2.82842 22.0237C2.40319 22.5239 2.01268 23.0559 1.63258 23.5955C0.880184 20.4922 0.864562 17.136 1.80787 14.0777C2.59584 11.5392 4.25336 9.31272 6.43591 8.02382C8.4553 6.79675 10.7593 6.32185 13.0044 5.82727C14.7313 5.43854 16.4964 5.07885 18.0819 4.20866C19.204 3.57546 20.2922 2.87293 21.3327 2.09454C22.0669 1.55126 22.7577 0.945214 23.4754 0.377576Z"
              fill="#18774F"
            />
          </svg>
          <Typography
            variant="display2"
            fontWeight="500"
            color="primary.800"
            my={3.75}
            sx={{
              fontFamily: 'Redaction, Manrope, serif',
              fontSize: '4rem',
              lineHeight: '93%',
            }}
          >
            Streamline your carbon project workflow today
          </Typography>
          <Typography
            variant="h3"
            color="primary.700"
            sx={{
              maxWidth: '412px',
              fontFamily: 'Aileron, Mulish, sans-serif',
              fontWeight: 400,
            }}
          >
            Take your impact and transparency to the next level with AirImpact's suite of carbon project management
            tools.
          </Typography>
        </Box>
      </Box>

      <Box maxWidth="480px" display="flex" width="100%" m="auto" justifyContent="center">
        {' '}
        {renderComponent()}
      </Box>
    </Box>
  )
}
